import { Timestamp, DocumentSnapshot, DocumentData } from "firebase/firestore";
import { z } from "zod";
import { dropUndefined } from "../utils/dropUndefined";
import { guardIsPlainObject } from "../utils/isPlainObject";
import { NotFoundError } from "../error-classes";

export enum LoyaltyProgramType {
  POINTS_BASED = "pointsBased",
  DISCOUNT_BASED = "discountBased",
  CASHBACK = "cashback",
  REFERRAL = "referral",
  RECURRING_DISCOUNT = "recurringDiscount",
}

export enum TriggerEvent {
  INVOICE_CREATED = "invoiceCreated",
  INVOICE_PAID = "invoicePaid",
  CUSTOMER_REFERRAL = "customerReferral",
  BUSINESS_REFERRAL = "businessReferral",
}

export enum RewardType {
  POINTS = "points",
  PERCENTAGE_DISCOUNT = "percentageDiscount",
  FIXED_DISCOUNT = "fixedDiscount",
  COMMISSION = "commission",
  FREE_SERVICE = "freeService",
}

export interface IntegrationConfig {
  // MoreRewards specific config
  moreRewards?: {
    enabled: boolean;
    offerCode: string;
  };
}

export interface LoyaltyProgramTemplate {
  // Basic Info
  name: string;
  description: string;
  isActive: boolean;

  // Program Configuration
  programType: LoyaltyProgramType;
  triggerEvent: TriggerEvent;

  // Reward Configuration
  rewardType: RewardType;
  rewardValue: number; // Points per dollar, discount percentage, etc.

  // Notification Settings
  sendLoyaltyNotificationEmail?: boolean;

  // Constraints
  maxRewardPerInvoice?: number;
  maxRewardPerJob?: number;
  maxRewardPerCustomer?: number;
  validityPeriod?: number; // days
  minimumSpend?: number;

  // Integration Configuration (for MoreRewards)
  integrationConfig?: IntegrationConfig;

  // Audit fields (auto-populated)
  timestampCreated: Timestamp;
  timestampLastModified: Timestamp;
  createdBy: string;
  lastModifiedBy: string;
}

export interface ExistingLoyaltyProgramTemplate extends LoyaltyProgramTemplate {
  id: string;
  refPath: string;
  siteKey: string;
}

// Zod schemas for validation
export const LoyaltyProgramTemplateSchema = z.object({
  name: z
    .string()
    .min(1, "Program name is required")
    .max(100, "Program name too long"),
  description: z
    .string()
    .min(1, "Description is required")
    .max(500, "Description too long"),
  isActive: z.boolean(),
  programType: z.nativeEnum(LoyaltyProgramType),
  triggerEvent: z.nativeEnum(TriggerEvent),
  rewardType: z.nativeEnum(RewardType),
  rewardValue: z.number().min(0, "Reward value must be positive"),
  sendLoyaltyNotificationEmail: z.boolean().optional(),
  maxRewardPerJob: z.number().min(0).optional(),
  maxRewardPerInvoice: z.number().min(0).optional(),
  maxRewardPerCustomer: z.number().min(0).optional(),
  validityPeriod: z.number().min(1).optional(),
  minimumSpend: z.number().min(0).optional(),
  integrationConfig: z
    .object({
      moreRewards: z
        .object({
          enabled: z.boolean(),
          offerCode: z
            .string()
            .min(1, "Offer code is required when MoreRewards is enabled"),
        })
        .optional(),
    })
    .optional()
    .refine(
      (config) => {
        if (config?.moreRewards?.enabled === true) {
          return (
            config.moreRewards.offerCode &&
            config.moreRewards.offerCode.length > 0
          );
        }
        return true;
      },
      {
        message:
          "Offer code is required when MoreRewards integration is enabled",
        path: ["moreRewards", "offerCode"],
      },
    ),
});

export type LoyaltyProgramTemplateFormData = z.infer<
  typeof LoyaltyProgramTemplateSchema
>;

// API types - matching the backend interfaces
export interface APICreateLoyaltyProgramRequest {
  siteKey: string;
  name: string;
  description: string;
  isActive: boolean;
  programType:
    | "pointsBased"
    | "discountBased"
    | "cashback"
    | "referral"
    | "recurringDiscount";
  triggerEvent:
    | "invoiceCreated"
    | "invoicePaid"
    | "customerReferral"
    | "businessReferral";
  rewardType:
    | "points"
    | "percentageDiscount"
    | "fixedDiscount"
    | "commission"
    | "freeService";
  rewardValue: number;
  sendLoyaltyNotificationEmail?: boolean;
  maxRewardPerJob?: number;
  maxRewardPerInvoice?: number;
  maxRewardPerCustomer?: number;
  validityPeriod?: number;
  minimumSpend?: number;
  integrationConfig?: {
    moreRewards?: {
      enabled: boolean;
      offerCode: string;
    };
  };
}

export interface APIUpdateLoyaltyProgramRequest {
  siteKey: string;
  programID: string;
  name?: string;
  description?: string;
  isActive?: boolean;
  programType?:
    | "pointsBased"
    | "discountBased"
    | "cashback"
    | "referral"
    | "recurringDiscount";
  triggerEvent?:
    | "invoiceCreated"
    | "invoicePaid"
    | "customerReferral"
    | "businessReferral";
  rewardType?:
    | "points"
    | "percentageDiscount"
    | "fixedDiscount"
    | "commission"
    | "freeService";
  rewardValue?: number;
  sendLoyaltyNotificationEmail?: boolean;
  maxRewardPerJob?: number;
  maxRewardPerInvoice?: number;
  maxRewardPerCustomer?: number;
  validityPeriod?: number;
  minimumSpend?: number;
  integrationConfig?: {
    moreRewards?: {
      enabled: boolean;
      offerCode: string;
    };
  };
}

export interface APIDeleteLoyaltyProgramRequest {
  siteKey: string;
  programID: string;
}

export interface APIActivateLoyaltyProgramRequest {
  siteKey: string;
  programID: string;
}

export interface APIGetLoyaltyProgramsRequest {
  siteKey: string;
}

export interface APIGetLoyaltyProgramRequest {
  siteKey: string;
  programID: string;
}

// Helper functions
export function getReadableProgramType(type: LoyaltyProgramType): string {
  switch (type) {
    case LoyaltyProgramType.POINTS_BASED:
      return "Points Based";
    case LoyaltyProgramType.DISCOUNT_BASED:
      return "Discount Based";
    case LoyaltyProgramType.CASHBACK:
      return "Cashback";
    case LoyaltyProgramType.REFERRAL:
      return "Referral";
    case LoyaltyProgramType.RECURRING_DISCOUNT:
      return "Recurring Discount";
    default:
      return type;
  }
}

export function getReadableTriggerEvent(trigger: TriggerEvent): string {
  switch (trigger) {
    case TriggerEvent.INVOICE_CREATED:
      return "Invoice Created";
    case TriggerEvent.INVOICE_PAID:
      return "Invoice Paid";
    case TriggerEvent.CUSTOMER_REFERRAL:
      return "Customer Referral";
    case TriggerEvent.BUSINESS_REFERRAL:
      return "Business Referral";
    default:
      return trigger;
  }
}

export function getReadableRewardType(type: RewardType): string {
  switch (type) {
    case RewardType.POINTS:
      return "Points";
    case RewardType.PERCENTAGE_DISCOUNT:
      return "Percentage Discount";
    case RewardType.FIXED_DISCOUNT:
      return "Fixed Discount";
    case RewardType.COMMISSION:
      return "Commission";
    case RewardType.FREE_SERVICE:
      return "Free Service";
    default:
      return type;
  }
}

// Manager class
export const LoyaltyProgramTemplateManager = {
  convertForFirestore: convertForFirestore,
  createFromSnapshot: createFromSnapshot,
  parse: validateLoyaltyProgramTemplate,
} as const;

function convertForFirestore(
  program: ExistingLoyaltyProgramTemplate | APIUpdateLoyaltyProgramRequest,
): DocumentData {
  const local = Object.assign({}, program);
  // Remove metadata fields that shouldn't be sent to Firestore
  delete (local as any).id;
  delete (local as any).refPath;
  delete (local as any).siteKey;
  delete (local as any).programID;
  return dropUndefined(local);
}

function createFromSnapshot(
  snapshot: DocumentSnapshot,
): ExistingLoyaltyProgramTemplate {
  if (!snapshot.exists()) {
    throw new NotFoundError("Document does not exist.");
  }

  const siteKey = snapshot.ref.path.split("/")[1]; // Extract siteKey from path

  return {
    id: snapshot.id,
    refPath: snapshot.ref.path,
    siteKey,
    ...validateLoyaltyProgramTemplate(snapshot.data()),
  };
}

function validateLoyaltyProgramTemplate(
  value: unknown,
): LoyaltyProgramTemplate {
  if (!guardIsPlainObject(value)) {
    throw new Error(`value not an object: ${value}`);
  }

  // Extend the schema to include audit fields for validation
  const fullSchema = LoyaltyProgramTemplateSchema.extend({
    timestampCreated: z.instanceof(Timestamp),
    timestampLastModified: z.instanceof(Timestamp),
    createdBy: z.string(),
    lastModifiedBy: z.string(),
  });

  return fullSchema.parse(value);
}
